import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Search, SlidersHorizontal } from 'lucide-react';
import CarCard from '../components/car-card';
import FilterSidebar from '../components/filter-sidebar';
import Pagination from '../components/pagination';
import useVehicleFilters from '../hooks/useVehicleFilters.js';
import NavBar from "../components/navigation";
import Footer from "../components/Footer";

const SearchBar = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
      <Input
        className="w-70 rounded-lg border border-border bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#c1392a] focus:ring-1 focus:ring-[#c1392a]/20"
        type="text"
        placeholder="Search any car..."
        value={value}
        onChange={onChange}
      />
    </div>
  
  </div>
);

const CarGrid = ({ cars, loading, error }) => {
  if (loading) {
    return <p className="py-20 text-center text-muted">Loading vehicles...</p>;
  }
  if (error) {
    return (
      <p className="py-20 text-center text-red-500">
        Could not load vehicles: {error}
      </p>
    );
  }
  if (cars.length === 0) {
    return (
      <p className="py-20 text-center text-muted">
        No vehicles match your filters.
      </p>
    );
  }
  return (
    <div className="grid gap-8 sm:grid-cols-2">
      {cars.map((car) => (
        <CarCard key={car.id} car={car} />
      ))}
    </div>
  );
};

export const InventoryPage = () => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const initialFilters = Object.fromEntries(searchParams.entries());
  const {
    filters,
    setFilter,
    toggleArrayFilter,
    resetFilters,
    page,
    setPage,
    totalPages,
    cars,
    loading,
    error,
  } = useVehicleFilters({ initialFilters });

  return (
    <>
    <NavBar />
    <div className="min-h-screen bg-background text-text">
      {/* Dashboard Header */}
      <section className="border-b border-border bg-white pt-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-center justify-between pb-4">
            <h1 className="font-heading text-3xl font-bold text-dark">
              Vehicle Listings
            </h1>
            <SearchBar
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="py-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <button
            type="button"
            className="mb-5 inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2 text-sm font-bold text-dark lg:hidden"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {filtersOpen ? 'Hide filters' : 'Show filters'}
          </button>
          <div className="grid gap-8 lg:grid-cols-4">
            <div className={`${filtersOpen ? 'block' : 'hidden'} lg:col-span-1 lg:block`}>
              <FilterSidebar
                filters={filters}
                setFilter={setFilter}
                toggleArrayFilter={toggleArrayFilter}
                resetFilters={resetFilters}
              />
            </div>
            <div className="lg:col-span-3">
              <CarGrid cars={cars} loading={loading} error={error} />
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
    <Footer />
    </>
  );
};

export default InventoryPage;